<script setup>
// @todo Remove legacyStyle and the legacyProps prop once the legacy theme is discontinued.
import { computed } from 'vue';

import { HrBtn, HrIcon, HrImg } from '../primitives';

/**
 * OAuth provider sign-in button with a provider logo image or font icon.
 *
 * @status recommended
 * @group actions
 * @useWhen Sign-in or authentication flows for third-party providers (Google, Apple, Microsoft)
 * @a11y Supply aria-label via attrs when the label prop is absent.
 */
defineOptions({ name: 'HrProviderBtn' });

const emit = defineEmits({
  /** Fired when the user activates the button. */
  click: null,
});

const props = defineProps({
  /** Button text label. */
  label: {
    type: String,
    default: null,
  },
  /** URL of the provider logo image (e.g. Google, Microsoft). Use `icon` for font-based icons. */
  logoSrc: {
    type: String,
    default: null,
  },
  /** Font icon name (e.g. "fab fa-apple"). Use `logoSrc` for image-based logos. */
  icon: {
    type: String,
    default: null,
  },
  /** Shows a loading spinner in place of the content. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Renders a pill-shaped skeleton placeholder instead of the button. */
  pending: {
    type: Boolean,
    default: false,
  },
});

// Icon-based providers (e.g. Apple) use a dark surface; image-based ones use a light surface.
const surfaceClass = computed(() => (props.icon ? 'hr-provider-btn--dark' : 'hr-provider-btn--light'));

const legacyStyle = computed(() => (props.icon
  ? { color: 'black', textColor: 'white' }
  : { color: 'white', textColor: 'grey-7' }));
</script>

<template>
  <hr-btn
    variant="outlined"
    :class="['hr-provider-btn', surfaceClass]"
    :loading="loading"
    :disable="disable"
    :pending="pending"
    :legacy-props="{ noCaps: true, ...legacyStyle }"
    @click="emit('click', $event)"
  >
    <span class="hr-provider-btn__content">
      <hr-img
        v-if="logoSrc"
        :src="logoSrc"
        width="24px"
      />
      <hr-icon
        v-else-if="icon"
        :name="icon"
        class="hr-provider-btn__icon"
      />
      <span v-if="label">{{ label }}</span>
    </span>
  </hr-btn>
</template>

<style lang="scss" scoped>
.hr-provider-btn__content {
  display: flex;
  align-items: center;
  gap: 8px; // M3 icon-label space
}

.hr-provider-btn__icon {
  font-size: 20px;
}

// Both classes land on the same q-btn element. Vue 3 applies the parent scope ID to child
// component roots even when inheritAttrs is false, so the compiled [data-v-xxx] attribute
// selector correctly targets q-btn here.
[data-theme] .hr-provider-btn--light.hr-btn--outlined {
  background: var(--app-comp-provider-btn-light-bg);
  color: var(--app-comp-provider-btn-light-color);
  --_btn-border: var(--app-comp-provider-btn-light-border);
}

[data-theme] .hr-provider-btn--dark.hr-btn--outlined {
  background: var(--app-comp-provider-btn-dark-bg);
  color: var(--app-comp-provider-btn-dark-color);
  --_btn-border: var(--app-comp-provider-btn-dark-bg);
}
</style>
